import { format, isAfter, isBefore, addHours, isToday, isTomorrow, isWeekend } from 'date-fns';

/**
 * Advanced suggestion generators for the Intelligence Dashboard
 * 
 * Each generator analyzes specific aspects of context to provide
 * intelligent, actionable suggestions with optimal timing.
 */

export class SuggestionGenerators {
  
  /**
   * Generate time-sensitive library and facility suggestions
   */
  static generateLibraryTimingSuggestions(context) {
    const suggestions = [];
    const { temporal } = context;
    const now = new Date();
    
    // Library closing soon - perfect for research/study
    if (temporal.hour >= 14 && temporal.hour < 17) {
      suggestions.push({
        type: 'timing-opportunity',
        priority: 'medium',
        title: 'Library Research Window',
        description: 'Libraries typically close at 6pm - perfect time for focused research',
        action: 'suggest-library-visit',
        timing: 'optimal',
        reasoning: 'Afternoon hours at library are usually quiet and productive',
        timeWindow: '2-3 hours available',
        confidence: 0.8
      });
    }
    
    // Weekend library hours - different timing
    if (isWeekend(now) && temporal.hour >= 10 && temporal.hour < 14) {
      suggestions.push({
        type: 'timing-opportunity',
        priority: 'low',
        title: 'Weekend Library Hours',
        description: 'Libraries often have reduced weekend hours - plan accordingly',
        action: 'check-library-hours',
        timing: 'good',
        reasoning: 'Weekend hours are often shorter, best to visit earlier',
        timeWindow: 'Limited weekend hours',
        confidence: 0.9
      });
    }
    
    return suggestions;
  }

  /**
   * Generate enhanced family coordination suggestions based on learned patterns
   */
  static generateFamilyCoordinationSuggestions(context) {
    const suggestions = [];
    const { temporal, family, personal } = context;
    
    // Parent availability analysis
    const parentBusyEvents = family.todayEvents?.filter(event => 
      event.participants?.some(p => p.isParent)
    ) || [];
    
    // Enhanced coordination timing based on success patterns
    const optimalCoordinationTimes = personal.optimalCoordinationTimes || [];
    const currentTimeKey = `${temporal.period}-${temporal.hour}`;
    
    if (optimalCoordinationTimes.includes(currentTimeKey)) {
      const confidence = this.calculateCoordinationConfidence(personal, currentTimeKey);
      
      suggestions.push({
        type: 'coordination',
        priority: 'high',
        title: 'Learned Optimal Coordination Time',
        description: 'Your coordination patterns show this time consistently works well for family communication',
        action: 'family-communication',
        timing: 'optimal',
        reasoning: `Based on your successful coordination history at this time`,
        timeWindow: 'Proven successful window',
        confidence: confidence,
        metadata: {
          patternBased: true,
          coordinationStyle: personal.coordinationStyle || 'collaborative',
          successHistory: personal.totalInteractions || 0
        }
      });
    }
    
    // Communication preference-based suggestions
    const preferredCommTimes = personal.preferredCommunicationTimes || [];
    const currentTimeWindow = `${temporal.period}-${temporal.hour}`;
    
    if (preferredCommTimes.includes(currentTimeWindow)) {
      suggestions.push({
        type: 'coordination',
        priority: 'medium',
        title: 'Your Preferred Communication Window',
        description: 'You typically communicate with family around this time',
        action: 'family-check-in',
        timing: 'optimal',
        reasoning: 'Based on your communication timing patterns',
        timeWindow: 'Personal preference window',
        confidence: 0.8,
        metadata: {
          communicationStyle: personal.communicationStyle || 'balanced',
          responseSpeed: personal.responseSpeed || {}
        }
      });
    }
    
    // Planning horizon adaptation
    const planningHorizon = personal.planningHorizon || 'medium';
    if (planningHorizon === 'long' && isWeekend(new Date()) && temporal.hour >= 9 && temporal.hour < 11) {
      suggestions.push({
        type: 'coordination',
        priority: 'high',
        title: 'Strategic Family Planning Time',
        description: 'Your planning style suggests this is perfect for long-term family coordination',
        action: 'family-activity-planning',
        timing: 'optimal',
        reasoning: `Matched to your ${planningHorizon}-term planning preference`,
        timeWindow: 'Extended planning session',
        confidence: 0.9,
        metadata: {
          planningType: 'long-term',
          suggestedDuration: '30-45 minutes'
        }
      });
    } else if (planningHorizon === 'short' && temporal.hour >= 16 && temporal.hour < 18) {
      suggestions.push({
        type: 'coordination',
        priority: 'medium',
        title: 'Quick Family Coordination',
        description: 'Perfect time for your preferred short-term family planning style',
        action: 'family-meal-coordination',
        timing: 'good',
        reasoning: `Matched to your ${planningHorizon}-term planning preference`,
        timeWindow: '10-15 minute coordination',
        confidence: 0.7,
        metadata: {
          planningType: 'short-term',
          suggestedDuration: '10-15 minutes'
        }
      });
    }
    
    // Activity preference-based suggestions
    const activityPreferences = personal.familyActivityPreferences || {};
    if (Object.keys(activityPreferences).length > 0 && isWeekend(new Date())) {
      const topActivity = Object.entries(activityPreferences)
        .sort(([,a], [,b]) => Object.values(b).reduce((sum, val) => sum + val, 0) - 
                              Object.values(a).reduce((sum, val) => sum + val, 0))[0];
      
      if (topActivity) {
        const [activityType] = topActivity;
        suggestions.push({
          type: 'coordination',
          priority: 'medium',
          title: 'Favorite Activity Opportunity',
          description: `Great time to suggest ${activityType} - one of your family's preferred activities`,
          action: 'suggest-preferred-activity',
          timing: 'good',
          reasoning: `Based on your family's activity preferences`,
          timeWindow: 'Weekend activity window',
          confidence: 0.8,
          metadata: {
            suggestedActivity: activityType,
            preferenceData: topActivity[1]
          }
        });
      }
    }
    
    // Adaptation confidence-based personalization
    if (personal.adaptationConfidence === 'high') {
      const coordinationAcceptance = personal.suggestionAcceptanceByType?.coordination?.rate || 0.5;
      
      if (coordinationAcceptance > 0.7 && temporal.hour >= 17 && temporal.hour < 19) {
        suggestions.push({
          type: 'coordination',
          priority: 'high',
          title: 'AI-Optimized Family Time',
          description: `Personalized suggestion: ${Math.round(coordinationAcceptance * 100)}% success rate for coordination at this time`,
          action: 'smart-family-coordination',
          timing: 'optimal',
          reasoning: 'AI has learned your family coordination patterns',
          timeWindow: 'Personalized optimal window',
          confidence: coordinationAcceptance,
          metadata: {
            adaptationLevel: 'high',
            historicalSuccessRate: coordinationAcceptance,
            suggestionType: 'ai-learned'
          }
        });
      }
    }
    
    return suggestions;
  }

  /**
   * Calculate coordination confidence based on pattern data
   */
  static calculateCoordinationConfidence(personal, timeKey) {
    const maturityFactor = personal.patternMaturity === 'mature' ? 0.9 : 
                          personal.patternMaturity === 'developing' ? 0.7 : 0.5;
    
    const interactionFactor = Math.min((personal.totalInteractions || 0) / 30, 1);
    const confidenceScore = personal.confidenceScores?.coordination || 0.5;
    
    return Math.min(maturityFactor * 0.4 + interactionFactor * 0.3 + confidenceScore * 0.3, 1);
  }

  /**
   * Generate academic and deadline awareness suggestions
   */
  static generateAcademicSuggestions(context) {
    const suggestions = [];
    const { academic, temporal } = context;
    
    // Urgent deadline awareness
    const urgentDeadlines = academic.upcomingDeadlines?.filter(deadline =>
      isAfter(new Date(deadline.date), new Date()) &&
      isBefore(new Date(deadline.date), addHours(new Date(), 48))
    ) || [];
    
    if (urgentDeadlines.length > 0) {
      suggestions.push({
        type: 'academic-urgent',
        priority: 'critical',
        title: 'Urgent Academic Deadline',
        description: `${urgentDeadlines[0].title} due ${format(new Date(urgentDeadlines[0].date), 'MMM d')}`,
        action: 'focus-on-deadline',
        timing: 'immediate',
        reasoning: 'Deadline approaching within 48 hours requires immediate attention',
        timeWindow: 'Immediate action needed',
        confidence: 1.0
      });
    }
    
    // High academic load management
    if (academic.academicLoad === 'high' || academic.academicLoad === 'very-high') {
      if (temporal.energy === 'high' && temporal.focus === 'productive') {
        suggestions.push({
          type: 'academic-management',
          priority: 'high',
          title: 'Peak Study Time',
          description: 'Your energy is high and you have a heavy academic load - tackle important work now',
          action: 'focus-study-session',
          timing: 'optimal',
          reasoning: 'High energy period during heavy academic load - maximize productivity',
          timeWindow: 'Current energy peak',
          confidence: 0.9
        });
      }
    }
    
    // Study break suggestions during high stress
    if (academic.stressLevel === 'high' && temporal.period === 'afternoon') {
      suggestions.push({
        type: 'academic-wellness',
        priority: 'medium',
        title: 'Academic Stress Break',
        description: 'High academic stress detected - consider a short break or physical activity',
        action: 'suggest-break-activity',
        timing: 'good',
        reasoning: 'Breaking up intense study periods improves overall performance',
        timeWindow: '15-30 minute break',
        confidence: 0.75
      });
    }
    
    return suggestions;
  }

  /**
   * Generate enhanced productivity suggestions based on learned patterns
   */
  static generateProductivitySuggestions(context) {
    const suggestions = [];
    const { personal, temporal, family } = context;
    
    // Enhanced productivity peak detection with confidence scoring
    if (personal.productivityPeaks?.includes(temporal.period)) {
      const confidence = this.calculateProductivityConfidence(personal, temporal.period);
      const productivityScore = personal.currentProductivityScore || 0.5;
      
      suggestions.push({
        type: 'productivity',
        priority: productivityScore > 0.8 ? 'high' : 'medium',
        title: 'Optimal Productivity Window',
        description: `Your productivity patterns suggest this is ideal time for focused work (${Math.round(productivityScore * 100)}% score)`,
        action: 'view-priority-tasks',
        timing: 'optimal',
        reasoning: `Based on ${personal.totalInteractions || 'limited'} interactions, you're typically ${Math.round(productivityScore * 100)}% productive during ${temporal.period}`,
        timeWindow: 'Current productivity peak',
        confidence: confidence,
        metadata: {
          productivityScore,
          patternMaturity: personal.patternMaturity || 'initial',
          optimalDuration: personal.optimalTaskDuration || 25
        }
      });
    }
    
    // Energy level prediction suggestions
    if (personal.predictedEnergyLevel === 'high' && personal.energyPatternReliability === 'high') {
      suggestions.push({
        type: 'productivity',
        priority: 'high',
        title: 'Predicted High Energy Period',
        description: 'Your energy patterns suggest you\'ll have high energy now - perfect for challenging tasks',
        action: 'tackle-complex-task',
        timing: 'optimal',
        reasoning: `Energy prediction based on ${personal.energyPatternReliability} reliability patterns`,
        timeWindow: `Next ${personal.optimalTaskDuration || 25} minutes`,
        confidence: 0.9
      });
    }
    
    // Focus session optimization
    const focusPrefs = personal.focusSessionPreferences || {};
    if (focusPrefs.optimalTimes?.includes(temporal.period) && focusPrefs.sessionCount >= 5) {
      suggestions.push({
        type: 'productivity',
        priority: 'medium',
        title: 'Focus Session Opportunity',
        description: `Your focus patterns suggest ${focusPrefs.preferredDuration}min sessions work well now`,
        action: 'start-focus-session',
        timing: 'optimal',
        reasoning: `Based on ${focusPrefs.sessionCount} recorded focus sessions`,
        timeWindow: `${focusPrefs.preferredDuration} minute session`,
        confidence: 0.8,
        metadata: {
          suggestedDuration: focusPrefs.preferredDuration,
          sessionHistory: focusPrefs.sessionCount
        }
      });
    }
    
    // Adaptation-based suggestions
    if (personal.adaptationConfidence === 'high' && personal.currentProductivityScore > 0.7) {
      const acceptanceRates = personal.suggestionAcceptanceByType || {};
      const productivityAcceptance = acceptanceRates.productivity?.rate || 0.5;
      
      if (productivityAcceptance > 0.7) {
        suggestions.push({
          type: 'productivity',
          priority: 'medium',
          title: 'Pattern-Optimized Task Time',
          description: 'AI has learned your preferences - this timing consistently works well for you',
          action: 'smart-task-recommendation',
          timing: 'optimal',
          reasoning: `${Math.round(productivityAcceptance * 100)}% acceptance rate for productivity suggestions`,
          timeWindow: 'Personalized optimal window',
          confidence: productivityAcceptance,
          metadata: {
            adaptationLevel: personal.adaptationConfidence,
            acceptanceHistory: acceptanceRates.productivity
          }
        });
      }
    }
    
    // Pattern maturity suggestions
    if (personal.patternMaturity === 'initial' && personal.totalInteractions < 20) {
      suggestions.push({
        type: 'productivity',
        priority: 'low',
        title: 'Build Your Productivity Pattern',
        description: 'Complete tasks now to help AI learn your optimal productivity times',
        action: 'pattern-building-task',
        timing: 'good',
        reasoning: 'Building productivity patterns improves future suggestions',
        timeWindow: 'Learning phase',
        confidence: 0.6,
        metadata: {
          interactionsNeeded: Math.max(20 - personal.totalInteractions, 0),
          learningPhase: true
        }
      });
    }
    
    return suggestions;
  }

  /**
   * Calculate productivity confidence based on pattern data
   */
  static calculateProductivityConfidence(personal, period) {
    const maturityFactor = personal.patternMaturity === 'mature' ? 0.9 : 
                          personal.patternMaturity === 'developing' ? 0.7 : 0.5;
    
    const interactionFactor = Math.min((personal.totalInteractions || 0) / 50, 1);
    const confidenceScore = personal.confidenceScores?.productivity || 0.5;
    
    return Math.min(maturityFactor * 0.4 + interactionFactor * 0.3 + confidenceScore * 0.3, 1);
  }

  /**
   * Generate shopping and errand coordination suggestions
   */
  static generateShoppingSuggestions(context, shoppingData) {
    const suggestions = [];
    const { temporal, family } = context;
    
    const activeItems = shoppingData?.filter(item => 
      !item.completed && !item.deleted && item.status === 'active'
    ) || [];
    
    // Shopping trip coordination
    if (activeItems.length >= 5) {
      if ((temporal.period === 'afternoon' && !isWeekend(new Date())) || 
          (isWeekend(new Date()) && temporal.hour >= 10 && temporal.hour < 16)) {
        suggestions.push({
          type: 'coordination',
          priority: 'medium',
          title: 'Shopping Trip Opportunity',
          description: `${activeItems.length} items ready - efficient time for a shopping trip`,
          action: 'coordinate-shopping-trip',
          timing: 'good',
          reasoning: 'Multiple items can be purchased together for efficiency',
          timeWindow: 'Current shopping window',
          confidence: 0.7
        });
      }
    }
    
    // Parent shopping coordination
    if (activeItems.length >= 3 && temporal.hour >= 17 && temporal.hour < 19) {
      suggestions.push({
        type: 'coordination',
        priority: 'low',
        title: 'Shopping List Review',
        description: 'Good time to discuss shopping list with parents for upcoming trips',
        action: 'discuss-shopping-list',
        timing: 'good',
        reasoning: 'Evening coordination helps plan efficient shopping trips',
        timeWindow: 'Family coordination time',
        confidence: 0.6
      });
    }
    
    return suggestions;
  }

  /**
   * Generate social and communication suggestions
   */
  static generateSocialSuggestions(context, messagesData) {
    const suggestions = [];
    const { temporal, family } = context;
    
    const unreadMessages = messagesData?.filter(msg => 
      !msg.read && isToday(new Date(msg.created_at))
    ) || [];
    
    // Unread family messages
    if (unreadMessages.length > 0) {
      const priority = unreadMessages.length > 3 ? 'high' : 'medium';
      suggestions.push({
        type: 'communication',
        priority,
        title: 'Unread Family Messages',
        description: `${unreadMessages.length} unread family messages - check when convenient`,
        action: 'check-family-messages',
        timing: 'when-convenient',
        reasoning: 'Family communication helps maintain coordination and connection',
        timeWindow: 'When you have a few minutes',
        confidence: 0.8
      });
    }
    
    // Family communication optimal timing
    if (temporal.period === 'evening' && temporal.focus === 'family-time') {
      suggestions.push({
        type: 'communication',
        priority: 'low',
        title: 'Family Connection Time',
        description: 'Good time for family conversations and sharing about your day',
        action: 'family-check-in',
        timing: 'optimal',
        reasoning: 'Evening is naturally when families connect and share',
        timeWindow: 'Family time period',
        confidence: 0.75
      });
    }
    
    return suggestions;
  }

  /**
   * Generate weather and environmental suggestions
   */
  static generateEnvironmentalSuggestions(context, weatherData) {
    const suggestions = [];
    const { temporal, environmental } = context;
    
    // Use actual weather data if available
    if (environmental && environmental.weather !== 'unknown') {
      const { temperature, outdoorSuitability, weatherRecommendation } = environmental;
      
      // Perfect weather suggestions
      if (outdoorSuitability === 'excellent' && temporal.hour >= 10 && temporal.hour < 18) {
        suggestions.push({
          type: 'weather-optimal',
          priority: 'high',
          title: 'Perfect Weather Alert',
          description: `${temperature}°F with ${environmental.conditions} - ideal for outdoor activities`,
          action: 'plan-outdoor-activity',
          timing: 'optimal',
          reasoning: 'Weather conditions are perfect for outdoor family time',
          timeWindow: 'Current excellent weather',
          confidence: 0.9
        });
      }
      
      // Rainy day suggestions
      if (environmental.weather === 'rain' && temporal.period === 'afternoon') {
        suggestions.push({
          type: 'weather-indoor',
          priority: 'medium',
          title: 'Rainy Day Activities',
          description: 'Perfect weather for cozy indoor family time',
          action: 'suggest-indoor-activities',
          timing: 'good',
          reasoning: 'Rain makes this a great time for indoor bonding',
          timeWindow: 'While it\'s raining',
          confidence: 0.8
        });
      }
      
      // Weekend weather planning
      if (isWeekend(new Date()) && outdoorSuitability === 'good') {
        suggestions.push({
          type: 'weather-weekend',
          priority: 'medium',
          title: 'Great Weekend Weather',
          description: `${temperature}°F - perfect for planned family activities`,
          action: 'review-weekend-plans',
          timing: 'good',
          reasoning: 'Good weather makes weekend activities more enjoyable',
          timeWindow: 'Weekend planning',
          confidence: 0.85
        });
      }
      
      // Hot weather warnings
      if (temperature > 85 && temporal.hour >= 11 && temporal.hour < 17) {
        suggestions.push({
          type: 'weather-caution',
          priority: 'medium',
          title: 'Hot Weather Advisory',
          description: `${temperature}°F - consider indoor activities or early evening outdoor time`,
          action: 'adjust-activity-timing',
          timing: 'important',
          reasoning: 'High temperatures can be uncomfortable for extended outdoor activities',
          timeWindow: 'Until temperature drops',
          confidence: 0.9
        });
      }
    }
    
    // Fallback suggestions when weather data unavailable
    if (!environmental || environmental.weather === 'unknown') {
      if (isWeekend(new Date()) && temporal.hour >= 10 && temporal.hour < 16) {
        suggestions.push({
          type: 'environmental',
          priority: 'low',
          title: 'Weekend Activity Time',
          description: 'Good time for outdoor family activities (weather permitting)',
          action: 'check-weather-and-plan',
          timing: 'good',
          reasoning: 'Weekend afternoon is typically ideal for outdoor family time',
          timeWindow: 'Afternoon window',
          confidence: 0.6
        });
      }
    }
    
    return suggestions;
  }

  /**
   * Generate goal and milestone suggestions
   */
  static generateGoalSuggestions(context, userData) {
    const suggestions = [];
    const { temporal, personal } = context;
    
    // Big 3 planning optimal timing
    if (temporal.period === 'morning' && temporal.energy === 'rising') {
      suggestions.push({
        type: 'planning',
        priority: 'high',
        title: 'Daily Planning Time',
        description: 'Perfect time to set your Big 3 priorities for the day',
        action: 'open-big3-planner',
        timing: 'optimal',
        reasoning: 'Morning planning sets intention and direction for the day',
        timeWindow: 'Morning planning window',
        confidence: 0.9
      });
    }
    
    // Evening reflection
    if (temporal.period === 'evening' && temporal.focus === 'family-time') {
      suggestions.push({
        type: 'reflection',
        priority: 'low',
        title: 'Daily Reflection',
        description: 'Good time to reflect on progress and plan for tomorrow',
        action: 'daily-reflection',
        timing: 'good',
        reasoning: 'Evening reflection helps consolidate learning and plan ahead',
        timeWindow: 'Wind-down period',
        confidence: 0.7
      });
    }
    
    return suggestions;
  }

  /**
   * Master suggestion aggregator
   */
  static generateAllSuggestions(context, familyData) {
    const allSuggestions = [
      ...this.generateLibraryTimingSuggestions(context),
      ...this.generateFamilyCoordinationSuggestions(context),
      ...this.generateAcademicSuggestions(context),
      ...this.generateProductivitySuggestions(context),
      ...this.generateShoppingSuggestions(context, familyData.shoppingItems),
      ...this.generateSocialSuggestions(context, familyData.messages),
      ...this.generateEnvironmentalSuggestions(context, familyData.weatherData),
      ...this.generateGoalSuggestions(context, familyData)
    ];

    // Remove duplicates and sort by priority and timing
    const uniqueSuggestions = this.deduplicateSuggestions(allSuggestions);
    return this.rankSuggestions(uniqueSuggestions);
  }

  /**
   * Remove duplicate suggestions based on type and action
   */
  static deduplicateSuggestions(suggestions) {
    const seen = new Set();
    return suggestions.filter(suggestion => {
      const key = `${suggestion.type}-${suggestion.action}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  /**
   * Rank suggestions by priority, timing, and confidence
   */
  static rankSuggestions(suggestions) {
    const priorityOrder = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };
    const timingOrder = { 'immediate': 4, 'optimal': 3, 'good': 2, 'when-convenient': 1 };
    
    return suggestions.sort((a, b) => {
      // Primary sort: priority
      const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
      if (priorityDiff !== 0) return priorityDiff;
      
      // Secondary sort: timing
      const timingDiff = timingOrder[b.timing] - timingOrder[a.timing];
      if (timingDiff !== 0) return timingDiff;
      
      // Tertiary sort: confidence
      return (b.confidence || 0.5) - (a.confidence || 0.5);
    });
  }
}

export default SuggestionGenerators;